import { useEffect, useState } from "react";
import { Product } from "../../types/Product";
import { useAuth } from "../useAuth";
import { useEnvironment } from "../useEnvironment";

type useProductProps = {
  data: any;
  productId: string | undefined;
  isTcrMode?: boolean;
  userType: string;
};

// compute normalizationThreshold to be used in bar formatter
export const normalizeAndCutOutliers = (
  arrayOfObject: any[],
  property: string,
) => {
  if (arrayOfObject.length) {
    var rawData = [...arrayOfObject]; // Copy
    var LENGTH = rawData.length;
    rawData = rawData.sort(function (a: any, b: any) {
      var aAbs = a ? Math.log(Math.abs(a[property])) : a;
      var bAbs = b ? Math.log(Math.abs(b[property])) : b;

      if (aAbs > bAbs) {
        return -1;
      }
      if (aAbs < bAbs) {
        return 1;
      }

      return 0;
    });

    var maxIndex = Math.min(LENGTH - 1, Math.floor((3 / 4) * (LENGTH + 1)));
    var minIndex = Math.floor((1 / 4) * (LENGTH + 1));
    var max = rawData[maxIndex][property];
    var min = rawData[minIndex][property];
    var IQR = max - min;
    var threshold = Math.abs(IQR * 1.5);

    for (var i = 0, length = arrayOfObject.length; i < length; i++) {
      arrayOfObject[i]["_s_normalizationThreshold"] = threshold;
    }
  }

  return arrayOfObject;
};

const peerWeightSort = (data: any, isTcrMode: boolean = false) => {
  if (isTcrMode) {
    // sort by rate (D->A), weight (10 -> 0)
    data.sort(function (a: any, b: any) {
      var sortByL1 = "rate";
      var sortByL2 = "weight";

      if (a[sortByL1] > b[sortByL1]) {
        return 1;
      }

      if (a[sortByL1] < b[sortByL1]) {
        return -1;
      }

      if (a[sortByL2] > b[sortByL2]) {
        return -1;
      }

      if (a[sortByL2] < b[sortByL2]) {
        return 1;
      }

      return 0;
    });
  } else {
    data.sort(function (a: any, b: any) {
      // sort by weight weight (10 -> 0)
      var sortByL1 = "weight";

      if (a[sortByL1] > b[sortByL1]) {
        return -1;
      }

      if (a[sortByL1] < b[sortByL1]) {
        return 1;
      }

      return 0;
    });
  }
};

const peerWeightData = (
  rawData: any,
  dataType: any,
  isTcrMode: boolean = false,
) => {
  var data = [];
  var item;

  for (let id in rawData) {
    item = rawData[id];
    data.push({
      cardinality: item["cardinality"],
      id: id,
      rate: item["TCR"],
      type: dataType,
      weight: item["weight"],
    });
  }

  peerWeightSort(data, isTcrMode);

  return data;
};

export const useProduct = ({
  data,
  productId,
  isTcrMode = false,
  userType,
}: useProductProps): Product | null => {
  const { isLogged } = useAuth();
  const { environment } = useEnvironment();
  const [product, setProduct] = useState<Product | null>(null);

  useEffect(() => {
    if (data != null) {
      let analytics: any = null;

      let benchmark: any = null;
      let calendarRebalance: any = null;
      let holdings: any = [];
      let info: any = null;
      let name: string = "";
      let peerWeights: any = {
        Country: [],
        CountryLong: [],
        CountryShort: [],
        Industry: [],
        IndustryShort: [],
        IndustryLong: [],
        Sector: [],
        SectorShort: [],
        SectorLong: [],
      };
      let priceList: any = [];
      let priceListLastDate: any;
      let priceBenchmark: any = [];
      let securities: any = {};
      let strategy: any = null;
      let type: string = "";

      let strategyAnalytics = {
        priceAnalytics: {},
        hPosAnalytics: {},
      };

      info = { ...data.info };

      name = info.name;

      type = data.type;

      // if (userType === TRIAL_USER_TYPE) {
      //   securities = data.dataForTrial?.fetch.reduce(
      //     (accumulator: any, item: any) => {
      //       accumulator[String(item.symbol)] = item;
      //       return accumulator;
      //     },
      //     {},
      //   );
      // } else {
      securities = data.fetch.reduce((accumulator: any, item: any) => {
        accumulator[String(item.symbol)] = item;
        return accumulator;
      }, {});
      // }

      benchmark = data.benchmark;

      let clusters;

      // let dateToShowInHoldingsWhenUserIsTrial: any = null;
      // if (userType === TRIAL_USER_TYPE) {
      //   if (data?.dataForTrial?.date)
      //     dateToShowInHoldingsWhenUserIsTrial = data.dataForTrial.date;

      //   clusters = data.dataForTrial.clusters;
      // } else {
      clusters = data.clusters;
      // }

      peerWeights.Country = peerWeightData(
        clusters.total.country,
        "where",
        isTcrMode,
      );

      // if (data.peerWeightsIndustryShort) {
      peerWeights.CountryShort = peerWeightData(
        clusters?.short?.country ?? {},
        "where",
        isTcrMode,
      );
      // }

      // if (data.peerWeightsIndustryLong) {
      peerWeights.CountryLong = peerWeightData(
        clusters?.long?.country ?? {},
        "where",
        isTcrMode,
      );
      // }

      peerWeights.Industry = peerWeightData(
        clusters.total.industry,
        "what",
        isTcrMode,
      );

      // if (data.peerWeightsIndustryShort) {
      peerWeights.IndustryShort = peerWeightData(
        clusters?.short?.industry ?? {},
        "what",
        isTcrMode,
      );
      // }

      // if (data.peerWeightsIndustryLong) {
      peerWeights.IndustryLong = peerWeightData(
        clusters?.long?.industry ?? {},
        "what",
        isTcrMode,
      );
      // }

      peerWeights.Sector = peerWeightData(
        clusters?.total?.sector ?? {},
        "what",
        isTcrMode,
      );

      // if (data.peerWeightsSectorShort) {
      peerWeights.SectorShort = peerWeightData(
        clusters?.short?.sector ?? {},
        "what",
        isTcrMode,
      );
      // }

      // if (data.peerWeightsSectorLong) {
      peerWeights.SectorLong = peerWeightData(
        clusters?.long?.sector ?? {},
        "what",
        isTcrMode,
      );
      // }

      if (isLogged) {
        // if (userType === TRIAL_USER_TYPE) {
        //   holdings = data.dataForTrial?.v.map((item: any) => ({
        //     ...securities[item.symbol],
        //     weight: item.weight,
        //   }));
        // } else {
        holdings = data.v.map((item: any) => ({
          ...securities[item.symbol],
          weight: item.weight,
        }));
        // }
      } else {
        let blockedHoldings = [];
        let vArr = [];
        // if (userType === TRIAL_USER_TYPE) {
        //   vArr = data.dataForTrial?.v;
        // } else {
        vArr = data?.v;
        // }
        for (let i = 0; i < vArr.length; i++) {
          if (i > 4) {
            break;
          }
          blockedHoldings.push(vArr[i]);
        }

        holdings = [];

        for (const item of blockedHoldings) {
          if (item.symbol in securities && securities[item.symbol] != null) {
            holdings.push({
              ...securities[item.symbol],
              weight: item.weight,
            });
          }
        }
      }

      calendarRebalance = data.calendarRebalance;

      priceList = data.H;

      priceListLastDate = data?.H?.[data?.H?.length - 1]?.d;

      priceBenchmark = data.B;

      strategyAnalytics.priceAnalytics = data?.priceAnalytics?.stats;
      strategyAnalytics.hPosAnalytics = data?.hPosAnalytics?.stats;

      // const strategies = new Strategies(environment);
      // strategy = strategies.decode(data.strategy);

      setProduct({
        analytics,
        benchmark,
        calendarRebalance,
        holdings,
        info,
        isTcrMode,
        name,
        peerWeights,
        priceList,
        priceListLastDate,
        priceBenchmark,
        productId: productId ?? "",
        trId: data.id,
        securities,
        strategy,
        type,
        strategyAnalytics,
        // dateToShowInHoldingsWhenUserIsTrial,
      } as Product);
    }
  }, [environment, data, productId, isLogged, isTcrMode, userType]);

  return product;
};
